import React, { useState, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { UploadIcon, CheckCircleIcon, AlertTriangleIcon, XCircleIcon, DownloadIcon, RefreshCwIcon, FileSpreadsheetIcon, ArrowRightIcon, ScaleIcon } from 'lucide-react';
import { formatNaira } from '../../utils/formatCurrency';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Table } from '../../components/ui/Table';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { MetricGrid } from '../../components/features/MetricCard';
// Mock reconciliation data
const mockDiscrepancies = [{
  id: 'DISC-001',
  type: 'missing',
  refundId: 'REF-001',
  ourAmount: 45000,
  airlineAmount: null,
  airline: 'Air Peace',
  status: 'open'
}, {
  id: 'DISC-002',
  type: 'mismatch',
  refundId: 'REF-002',
  ourAmount: 52000,
  airlineAmount: 50000,
  airline: 'Arik Air',
  status: 'investigating'
}, {
  id: 'DISC-003',
  type: 'duplicate',
  refundId: 'REF-003',
  ourAmount: 38000,
  airlineAmount: 76000,
  airline: 'Dana Air',
  status: 'resolved'
}];
// Upload dropzone component
function UploadDropzone({
  onUpload,
  isUploading
}: {
  onUpload: () => void;
  isUploading: boolean;
}) {
  const [isDragging, setIsDragging] = useState(false);
  return <motion.div className={`relative border-2 border-dashed rounded-2xl p-8 text-center transition-all ${isDragging ? 'border-primary-500 bg-primary-50' : 'border-surface-200 hover:border-primary-300 hover:bg-surface-50'}`} onDragOver={e => {
    e.preventDefault();
    setIsDragging(true);
  }} onDragLeave={() => setIsDragging(false)} onDrop={e => {
    e.preventDefault();
    setIsDragging(false);
    onUpload();
  }} animate={isDragging ? {
    scale: 1.02
  } : {
    scale: 1
  }} data-id="element-799">
      <motion.div className="w-16 h-16 mx-auto mb-4 bg-primary-100 rounded-2xl flex items-center justify-center" animate={isDragging ? {
      y: -5,
      scale: 1.1
    } : {
      y: 0,
      scale: 1
    }} data-id="element-800">
        <FileSpreadsheetIcon className="w-8 h-8 text-primary-600" data-id="element-801" />
      </motion.div>
      <h3 className="text-heading-md font-semibold text-surface-900 mb-2" data-id="element-802">
        Upload Airline Statement
      </h3>
      <p className="text-body-md text-surface-600 mb-6 max-w-md mx-auto" data-id="element-803">
        Drag and drop a CSV or Excel file, or click to browse. We'll
        automatically match transactions.
      </p>
      <motion.div whileHover={{
      scale: 1.02
    }} whileTap={{
      scale: 0.98
    }} data-id="element-804">
        <Button icon={<UploadIcon className="w-4 h-4" data-id="element-806" />} onClick={onUpload} loading={isUploading} className="shadow-lg shadow-primary-500/20" data-id="element-805">
          {isUploading ? 'Processing...' : 'Select File'}
        </Button>
      </motion.div>
      <p className="text-body-sm text-surface-500 mt-4" data-id="element-807">
        Supported formats: CSV, XLSX, XLS
      </p>
    </motion.div>;
}
// Reconciliation progress component
function ReconciliationProgress({
  isActive
}: {
  isActive: boolean;
}) {
  const steps = [{
    label: 'Upload',
    status: 'complete'
  }, {
    label: 'Parse',
    status: 'complete'
  }, {
    label: 'Match',
    status: isActive ? 'current' : 'pending'
  }, {
    label: 'Review',
    status: 'pending'
  }];
  return <div className="flex items-center justify-center gap-2 py-4" data-id="element-808">
      {steps.map((step, index) => <Fragment key={step.label} data-id="element-809">
          <motion.div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${step.status === 'complete' ? 'bg-success-100 text-success-700' : step.status === 'current' ? 'bg-primary-100 text-primary-700' : 'bg-surface-100 text-surface-500'}`} animate={step.status === 'current' ? {
        scale: [1, 1.05, 1]
      } : {}} transition={{
        duration: 1.5,
        repeat: Infinity
      }} data-id="element-810">
            {step.status === 'complete' && <CheckCircleIcon className="w-4 h-4" data-id="element-811" />}
            {step.status === 'current' && <motion.div animate={{
          rotate: 360
        }} transition={{
          duration: 1,
          repeat: Infinity,
          ease: 'linear'
        }} data-id="element-812">
                <RefreshCwIcon className="w-4 h-4" data-id="element-813" />
              </motion.div>}
            <span className="text-body-sm font-medium" data-id="element-814">{step.label}</span>
          </motion.div>
          {index < steps.length - 1 && <ArrowRightIcon className="w-4 h-4 text-surface-300" data-id="element-815" />}
        </Fragment>)}
    </div>;
}
export function ReconciliationPage() {
  const [isUploading, setIsUploading] = useState(false);
  const [isMatching, setIsMatching] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const metrics = [{
    label: 'Matched Transactions',
    value: '1,234',
    change: {
      value: 98.5,
      trend: 'up' as const
    },
    icon: <CheckCircleIcon className="w-5 h-5" data-id="element-816" />,
    iconColor: 'bg-success-100 text-success-600'
  }, {
    label: 'Discrepancies',
    value: '12',
    change: {
      value: -3,
      trend: 'down' as const
    },
    icon: <AlertTriangleIcon className="w-5 h-5" data-id="element-817" />,
    iconColor: 'bg-warning-100 text-warning-600'
  }, {
    label: 'Missing Refunds',
    value: '5',
    icon: <XCircleIcon className="w-5 h-5" data-id="element-818" />,
    iconColor: 'bg-error-100 text-error-600'
  }, {
    label: 'Amount Variance',
    value: formatNaira(125000),
    icon: <ScaleIcon className="w-5 h-5" data-id="element-819" />,
    iconColor: 'bg-primary-100 text-primary-600'
  }];
  const columns = [{
    key: 'id',
    header: 'ID'
  }, {
    key: 'type',
    header: 'Type',
    render: (value: unknown) => {
      const type = value as string;
      const colors = {
        missing: 'bg-error-100 text-error-700',
        mismatch: 'bg-warning-100 text-warning-700',
        duplicate: 'bg-primary-100 text-primary-700'
      };
      return <span className={`px-2.5 py-1 rounded-lg text-caption font-semibold capitalize ${colors[type as keyof typeof colors]}`} data-id="element-820">
            {type}
          </span>;
    }
  }, {
    key: 'refundId',
    header: 'Refund ID'
  }, {
    key: 'airline',
    header: 'Airline'
  }, {
    key: 'ourAmount',
    header: 'Our Amount',
    render: (value: unknown) => <span className="tabular-nums font-semibold text-surface-900" data-id="element-821">
          {formatNaira(value as number)}
        </span>
  }, {
    key: 'airlineAmount',
    header: 'Airline Amount',
    render: (value: unknown, row: (typeof mockDiscrepancies)[0]) => {
      const amount = value as number | null;
      const hasVariance = amount && row.ourAmount !== amount;
      return <span className={`tabular-nums font-semibold ${hasVariance ? 'text-error-600' : 'text-surface-900'}`} data-id="element-822">
            {amount ? formatNaira(amount) : '—'}
          </span>;
    }
  }, {
    key: 'status',
    header: 'Status',
    render: (value: unknown) => {
      const status = value as string;
      return <StatusBadge status={status === 'resolved' ? 'complete' : status === 'investigating' ? 'processing' : 'pending'} size="sm" data-id="element-823">
            {status}
          </StatusBadge>;
    }
  }];
  const handleUpload = async () => {
    setIsUploading(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsUploading(false);
    setIsMatching(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    setIsMatching(false);
  };
  const handleExport = async () => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsExporting(false);
  };
  return <div className="space-y-6" data-id="element-824">
      {/* Metrics */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} data-id="element-825">
        <MetricGrid metrics={metrics} columns={4} data-id="element-826" />
      </motion.div>

      {/* Upload Section */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.1
    }} data-id="element-827">
        <Card variant="elevated" padding="lg" className="shadow-lg border-0" data-id="element-828">
          <div className="flex items-center justify-between mb-6" data-id="element-829">
            <div data-id="element-830">
              <h2 className="text-heading-lg font-bold text-surface-900" data-id="element-831">
                Reconciliation
              </h2>
              <p className="text-body-sm text-surface-500 mt-1" data-id="element-832">
                Upload airline statements to match transactions
              </p>
            </div>
            {(isUploading || isMatching) && <ReconciliationProgress isActive={isMatching} data-id="element-833" />}
          </div>
          <UploadDropzone onUpload={handleUpload} isUploading={isUploading || isMatching} data-id="element-834" />
        </Card>
      </motion.div>

      {/* Discrepancies */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2
    }} data-id="element-835">
        <Card variant="elevated" padding="none" className="shadow-lg border-0 overflow-hidden" data-id="element-836">
          <div className="p-6 border-b border-surface-100 flex items-center justify-between" data-id="element-837">
            <div data-id="element-838">
              <h2 className="text-heading-lg font-bold text-surface-900" data-id="element-839">
                Discrepancies
              </h2>
              <p className="text-body-sm text-surface-500 mt-1" data-id="element-840">
                Transactions that need review
              </p>
            </div>
            <div className="flex items-center gap-2" data-id="element-841">
              <motion.div whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} data-id="element-842">
                <Button variant="secondary" size="sm" icon={<RefreshCwIcon className="w-4 h-4" data-id="element-844" />} data-id="element-843">
                  Re-match
                </Button>
              </motion.div>
              <motion.div whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} data-id="element-845">
                <Button variant="secondary" size="sm" icon={<DownloadIcon className="w-4 h-4" data-id="element-847" />} onClick={handleExport} loading={isExporting} data-id="element-846">
                  Export
                </Button>
              </motion.div>
            </div>
          </div>
          <Table columns={columns} data={mockDiscrepancies} keyExtractor={row => row.id} data-id="element-848" />
        </Card>
      </motion.div>
    </div>;
}